import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Box, Flex } from '@dutchie/common/components/rebass-compat';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useConsumerLoginV2 } from 'src/state/actions/users';
import { Button } from 'shared/components';
import { DefaultInput } from 'shared/components/text-input-with-label';
import {
  CheckoutBody,
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import useErnie from 'shared/hooks/use-ernie';
import { Divider } from 'src/components/divider';
import { GoogleLogin, AppleLogin } from 'src/components/social-login';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS } from 'shared/constants/turnstile';

async function handleLogin(
  setLoading,
  Cart,
  setInputFieldError,
  showErnie,
  consumerLogin,
  executeAndWaitForToken,
  flags
) {
  setLoading(true);
  // TODO: move this to a validator
  const canSubmit = Cart.validateAuth();

  if (!canSubmit.proceed) {
    const { errors } = canSubmit;
    setInputFieldError([...errors]);

    if (errors?.length === 1) {
      showErnie(errors[0].message, `danger`);
    } else {
      showErnie(`Please enter a valid email and password`, `danger`);
    }

    setLoading(false);
    return;
  }

  let turnstileToken = '';
  // Operational flag: can be toggled off to disable turnstile validation if issues arise
  if (flags['growth.ecomm.login-turnstile-enable.rollout']) {
    try {
      turnstileToken = await executeAndWaitForToken();
    } catch (_error) {
      showErnie('Error validating your request. Please try again.', 'danger');
      setLoading(false);
      return;
    }
  }

  const result = await consumerLogin({
    email: Cart.order?.inlineAuth?.email,
    password: Cart.order?.inlineAuth?.password,
    turnstileToken,
    dispensaryId: Cart.dispensary?.id,
  });

  if (result.success) {
    if (!result?.sso) {
      showErnie(`You've been successfully logged in as ${Cart.order?.inlineAuth?.email}`, 'success');
    }
  } else {
    showErnie('Incorrect email or password', 'danger');
  }
  setLoading(false);
}

function InlineLogin({ onShowSignup, inputFieldError, setInputFieldError, handleInputChange }) {
  const { Cart, Modals } = useStores();
  const Checkout = useCheckout();
  const showErnie = useErnie();
  const [loading, setLoading] = useState(false);
  const consumerLogin = useConsumerLoginV2();
  const flags = useFlags();

  const { TurnstileComponent, executeAndWaitForToken } = useTurnstile({
    size: 'flexible',
    action: TURNSTILE_ACTIONS.LOGIN,
  });

  useEffect(() => {
    Cart.updateInlineAuth('password', '');
    return Cart.updateInlineAuth('password', '');
  }, [Cart]);

  function handleShowSignup() {
    const { inlineAuth } = Cart?.order;
    Checkout.removeError({ path: 'inlineAuth' });
    if (inlineAuth) {
      inlineAuth.email = '';
      inlineAuth.password = '';
    }
    onShowSignup();
  }

  // FIXME: we should use the ModalNames type. was not able to import from shared
  const handleOpenResetPasswordModal = () => Modals.openModal(`resetPassword`);

  const inlineAuthError = Checkout.getError({ card: 'inline-auth' }).error;
  const removeInlineAuthError = () => Checkout.removeError({ card: 'inline-auth' });
  const getInputFieldError = (field) => inlineAuthError || _.find(inputFieldError, ['field', field]);

  return (
    <CheckoutLabelGrouping id='inlineAuth'>
      <CheckoutLabel card>
        <CheckoutSectionHeading pt='14px'>Account</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        <Box pt='16px' pb='20px'>
          <InstructionText>
            To continue, login to your account. Don't have one?{' '}
            <SignupButton tabIndex={0} onClick={handleShowSignup}>
              Signup
            </SignupButton>
          </InstructionText>
        </Box>
        <Container>
          <SocialLogins>
            <GoogleLogin redirectUrl={window.location.href} />
            <AppleLogin redirectUrl={window.location.href} />
          </SocialLogins>

          <Divider text='or enter your information' />

          <DefaultInput
            onValueAfterError={removeInlineAuthError}
            error={getInputFieldError('email')}
            errorMessage={Checkout.getError({ path: 'email' }).message}
            name='email'
            label='Email'
            onChange={handleInputChange}
            value={Cart.order?.inlineAuth?.email}
            mb='19px'
            mt={0}
            width='100%'
            data-cy='checkout-login-email-input'
            data-test='checkout-login-email-input'
            data-testid='checkout-login-email-input'
            autoComplete='email'
          />
          <DefaultInput
            onValueAfterError={removeInlineAuthError}
            error={getInputFieldError('password')}
            errorMessage={Checkout.getError({ path: 'password' }).message}
            name='password'
            type='password'
            label='Password'
            onChange={handleInputChange}
            value={Cart.order?.inlineAuth?.password}
            width='100%'
            data-cy='checkout-login-password-input'
            data-test='checkout-login-password-input'
            data-testid='checkout-login-password-input'
            autoComplete='current-password'
          />

          <Box mt='20px'>{flags['growth.ecomm.login-turnstile-enable.rollout'] && <TurnstileComponent />}</Box>

          <Flex alignItems='center' justifyContent='space-between' mt='22px' mb='8px'>
            <Button
              onClick={() =>
                handleLogin(
                  setLoading,
                  Cart,
                  setInputFieldError,
                  showErnie,
                  consumerLogin,
                  executeAndWaitForToken,
                  flags
                )
              }
              loading={loading}
              disabled={loading}
              data-cy='checkout-login-confirm-button'
              data-test='checkout-login-confirm-button'
              data-testid='checkout-login-confirm-button'
            >
              Login
            </Button>
            {/* eslint-disable-next-line styled-components-a11y/anchor-is-valid, styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions */}
            <ForgotText as='a' onClick={handleOpenResetPasswordModal}>
              Forgot your password?
            </ForgotText>
          </Flex>
        </Container>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(InlineLogin);

const InstructionText = styled.div`
  color: var(--dcm-checkout-text-color, #6b6f73);
  font-size: 13px;
  line-height: normal;

  a {
    color: var(--dcm-checkout-link-color);
    cursor: pointer;
  }
`;

const Container = styled.div`
  max-width: 390px;
`;

const SocialLogins = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ForgotText = styled(InstructionText)`
  color: var(--dcm-checkout-text-muted-color, #8f989e);
  cursor: pointer;
`;
const SignupButton = styled.button`
  background: none;
  border: none;
  color: var(--dct--customized-colors-buttonsLinks);
  cursor: pointer;
  padding-left: 0;
`;
